import { useQuery } from "@tanstack/react-query";
import type { CurrentUser } from "@silph-scope/shared";

export type AuthMode = "disabled" | "parsec";
export type AuthGateStatus = "loading" | "disabled" | "anon" | "unprovisioned" | "ok";

export interface AuthConfig {
  mode: AuthMode;
  disabled: boolean;
  loginUrl: string;
  logoutUrl: string;
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const res = await fetch("/api/auth/config", { credentials: "include" });
  if (!res.ok) throw new Error(`auth config failed: ${res.status}`);
  return res.json() as Promise<AuthConfig>;
}

/** Raw fetch — must not auto-redirect on 401, or the Sign In screen never renders. */
export async function fetchAuthMe(): Promise<{ status: Exclude<AuthGateStatus, "loading" | "disabled">; user: CurrentUser | null; email: string | null }> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (res.status === 401) return { status: "anon", user: null, email: null };
  if (res.status === 403) {
    const body = (await res.json().catch(() => ({}))) as { email?: string };
    return { status: "unprovisioned", user: null, email: body.email ?? null };
  }
  if (!res.ok) throw new Error(`auth me failed: ${res.status}`);
  const user = (await res.json()) as CurrentUser;
  return { status: "ok", user, email: user.email };
}

export function useAuthSession() {
  return useQuery({
    queryKey: ["auth-session"],
    queryFn: async () => {
      const cfg = await fetchAuthConfig();
      if (cfg.disabled) return { cfg, status: "disabled" as const, user: null, email: null };
      const me = await fetchAuthMe();
      return { cfg, ...me };
    },
    retry: false,
    staleTime: 30_000
  });
}
